import React from "react";
import { ArrowLeft, Plus } from "lucide-react";

/**
 * @param {string} title - பக்கத்தின் முக்கிய தலைப்பு
 * @param {string} description - பக்கத்தின் துணை விளக்கம் (Optional)
 * @param {function} onBack - பின்செல்லும் செயல்பாடு (Optional)
 * @param {object|React.ReactNode} titleIcon - தலைப்பிற்கு அருகில் வரும் Icon (Optional)
 * @param {object|React.ReactNode} action - வலது பக்க செயல்பாட்டுப் பொத்தான் (Optional)
 * @param {boolean} isSticky - ஸ்க்ரோல் செய்யும்போது ஹெடர் ஒட்டிக்கொள்ள வேண்டுமா (Default: true)
 * @param {string} topOffset - டேப் இருக்கும் பக்கங்களில் ஒட்ட வேண்டிய உயரம் (e.g., "top-[54px]")
 */
const PageHeader = ({
  title,
  description,
  onBack,
  titleIcon,
  action,
  isSticky = true,
  topOffset = "top-0", 
}) => {
  
  const renderActionIcon = () => {
    if (!action || !action.icon) return <Plus strokeWidth={2} />;
    if (React.isValidElement(action.icon)) return action.icon;
    const IconComponent = action.icon;
    return <IconComponent strokeWidth={2} size={20} />;
  };

  return (
    <div className={`
      w-full bg-white/95 backdrop-blur-md rounded-xl p-4 mb-6
      flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 
      transition-all duration-300 ease-in-out border border-gray-200 shadow-sm
      }
    `}>
      {/* இடது பக்கம்: Back Button, Title Icon மற்றும் Title */}
      <div className="flex items-center gap-4 min-w-0">
        {onBack && (
          <button
            onClick={onBack}
            className="
              flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl 
              bg-red-50 hover:bg-red-100/80 text-primary border border-red-100/30 
              transition-all active:scale-95 duration-200
            "
            aria-label="Go back"
          >
            <ArrowLeft className="w-4 h-4 stroke-[2.5]" />
          </button>
        )}

        {/* Title & Description Block */}
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2.5">
            {titleIcon && (
              <span className="text-primary flex-shrink-0 flex items-center justify-center">
                {React.isValidElement(titleIcon) ? (
                  titleIcon
                ) : (
                  React.createElement(titleIcon, { className: "w-5 h-5 stroke-[2]" })
                )}
              </span>
            )}
            <h2 className="text-lg md:text-2xl font-bold text-gray-900 leading-tight tracking-tight truncate">
              {title}
            </h2>
          </div>
          
          {description && (
            <p className="text-xs text-gray-400 font-medium mt-1">
              {description}
            </p>
          )}
        </div>
      </div>

      {/* வலது பக்கம்: Action Button */}
      {action && (
        <div className="w-full sm:w-auto flex-shrink-0">
          {React.isValidElement(action) ? (
            action
          ) : (
            <button
              onClick={action.onClick}
              className={`
                flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 
                rounded-lg font-bold text-sm transition-all duration-200 shadow-sm hover:shadow
                ${action.variant === "secondary" 
                  ? "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                  : "bg-primary text-white hover:bg-secondary"
                }
              `}
            >
              {renderActionIcon()}
              <span>{action.label}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PageHeader;